import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { friendlyMessage } from '@/lib/errors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { authApi } from '@/lib/api/services/auth';
import { useAuth } from '@/components/AuthProvider';
import { validatePhoneForCountry, phoneOverLengthError } from '@/lib/constants/phoneValidation';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { OtpInput } from '@/components/OtpInput';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { LandingTermsOfService } from '@/pages/LandingTermsOfService';
import { markNewSignupPending } from '@/lib/onboardingCompletion';
import { ONBOARDING_V2_PREFLIGHT_PATH } from '@/components/onboarding-v2/onboardingV2Routes';
import { cn } from '@/lib/utils';

/** Screen 1 phone → Screen 2 code → Screen 3 name (new accounts only). */
export type PhoneAuthStep = 'phone' | 'otp' | 'name';

/** Imperative handle so Auth.tsx's header back arrow can drive this flow. */
export interface PhoneAuthFlowHandle {
  back: () => void;
}

interface PhoneAuthFlowProps {
  /** Existing account with 2FA — a TOTP code is still required. */
  onRequiresTotp: (userId: string) => void;
  /** Fully successful login (tokens already stored). */
  onSuccess: () => void;
  /** Reports the live step (+ display phone) so the parent can render its own
   *  back arrow and the "code sent to <phone>" subtitle above a shared header. */
  onStepChange?: (step: PhoneAuthStep, phoneNumberDisplay?: string) => void;
  /** One phone maps to several accounts — OTP cannot disambiguate, so the user
   *  is steered to email + password. */
  onSwitchToPassword?: (message: string) => void;
}

/**
 * The single phone-first entry point for /auth.
 *
 * One phone field is both login and signup: the send resolves whether the
 * number already has an account (`purpose:'auto'`) and the flow branches on that
 * answer AFTER the code arrives but BEFORE it is submitted.
 *
 * 🔴 The branch MUST be resolved before the code is submitted, and the login
 * branch must NEVER call /verify-otp. /verify-otp sets `is_verified`, while
 * `login-otp` resolves the code via `findLatestUnverifiedPhoneOtp` — verifying
 * first silently burns the code and the login then fails with INVALID_OTP.
 * That is why the existence check happens at send time rather than here.
 */
export const PhoneAuthFlow = forwardRef<PhoneAuthFlowHandle, PhoneAuthFlowProps>(
  function PhoneAuthFlow({ onRequiresTotp, onSuccess, onStepChange, onSwitchToPassword }, ref) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loginWithOtp, signupWithPhone } = useAuth();

    const [step, setStep] = useState<PhoneAuthStep>('phone');
    const [countryCode, setCountryCode] = useState('+91');
    const [phoneDisplay, setPhoneDisplay] = useState('');
    const [otpCode, setOtpCode] = useState('');
    // Screen 3 collects the ORGANIZATION name, not a person's name. It is sent
    // as both `organization_name` (→ organizations.name, verbatim) and
    // `full_name` (→ profiles.full_name), because signup collects exactly one
    // name and profiles.full_name is what the audit log and scheduling emails
    // read for identity — a phone-first account has no email to fall back to,
    // so leaving it empty makes those surfaces render "System" / "Host".
    const [organizationName, setOrganizationName] = useState('');
    // Resolved at send time from the `auto` response. null until resolved — the
    // flow refuses to submit a code while it is null, because guessing the
    // branch either burns the code (verify on a login row) or 401s.
    const [userExists, setUserExists] = useState<boolean | null>(null);

    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [creating, setCreating] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [showTermsDialog, setShowTermsDialog] = useState(false);

    // Field-level errors, rendered directly under each input.
    const [phoneError, setPhoneError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [nameError, setNameError] = useState('');

    // E.164 for the API (no space); a spaced form for display only.
    const digits = phoneDisplay.replace(/\D/g, '');
    const phoneNumber = `${countryCode}${digits}`;
    const phoneNumberDisplay = `${countryCode} ${digits}`;

    // Guards the auto-submit so one completed code is submitted exactly once,
    // even across the render in which `verifying` has not flipped yet.
    const submittedCodeRef = useRef('');

    useEffect(() => {
      if (countdown > 0) {
        const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
        return () => clearTimeout(t);
      }
    }, [countdown]);

    // Report the live step so the parent can toggle its back arrow + subtitle.
    useEffect(() => {
      onStepChange?.(step, phoneNumberDisplay);
    }, [step, phoneNumberDisplay, onStepChange]);

    /** Full reset back to Screen 1. Used by the back arrow and by every
     *  unrecoverable-code path (a burned/expired/mis-stamped code can only be
     *  replaced by a fresh send). */
    const resetToPhone = (message = '') => {
      setStep('phone');
      setOtpCode('');
      setOtpError('');
      setNameError('');
      setCountdown(0);
      // Cleared so a different number re-resolves instead of inheriting this
      // number's branch.
      setUserExists(null);
      submittedCodeRef.current = '';
      setPhoneError(message);
    };

    useImperativeHandle(
      ref,
      () => ({
        // From the code screen, back returns to the phone screen. From the name
        // screen it ALSO returns to the phone screen: the code has already been
        // verified, so re-submitting it would 401 via _resolvePhoneOtpWithLockout
        // (the row is no longer unverified). A fresh code is the only way on.
        back: () => resetToPhone(),
      }),
      [],
    );

    // ── Screen 1 → send the code and resolve the branch ────────────────────
    const handleSendOtp = async () => {
      const pErr = validatePhoneForCountry(countryCode, phoneDisplay);
      if (pErr) {
        setPhoneError(pErr);
        return;
      }
      setPhoneError('');
      setSending(true);
      try {
        // 'auto': skips both existence gates, stamps the OTP row with the
        // resolved purpose, and reports which branch this number is on.
        const result = await authApi.sendOTP(phoneNumber, undefined, undefined, 'auto');
        if (!result.data) {
          setPhoneError(result.error || 'Failed to send OTP');
          return;
        }
        if (typeof result.data.user_exists !== 'boolean') {
          // Without a definite answer the branch is unknowable, and picking one
          // would burn the code. Fail visibly instead of guessing.
          setPhoneError('Could not start verification. Please try again.');
          return;
        }
        setUserExists(result.data.user_exists);
        setOtpCode('');
        submittedCodeRef.current = '';
        setStep('otp');
        setCountdown(60);
        toast({
          variant: 'success',
          title: 'OTP Sent!',
          description: 'Check your WhatsApp for the verification code.',
        });
      } catch (err: any) {
        setPhoneError(friendlyMessage(err, 'Failed to send OTP'));
      } finally {
        setSending(false);
      }
    };

    // ── Resend — pinned to the ALREADY-RESOLVED purpose ────────────────────
    // Deliberately not 'auto': re-resolving could flip the branch mid-flow (the
    // user would be on the code screen for one branch holding a row stamped for
    // the other). Pinning also turns the two race outcomes into clean signals —
    // 409 PHONE_EXISTS means an account appeared, 404 PHONE_NOT_FOUND means one
    // disappeared — both of which restart the flow.
    const handleResend = async () => {
      if (userExists === null) {
        resetToPhone('Please re-enter your phone number.');
        return;
      }
      setOtpError('');
      setSending(true);
      try {
        const result = await authApi.sendOTP(
          phoneNumber,
          undefined,
          undefined,
          userExists ? 'login' : 'signup',
        );
        if (!result.data) {
          if (result.errorCode === 'PHONE_EXISTS') {
            resetToPhone('This number now has an account. Please continue to log in.');
            return;
          }
          if (result.errorCode === 'PHONE_NOT_FOUND') {
            resetToPhone('This number no longer has an account. Please start again.');
            return;
          }
          setOtpError(result.error || 'Failed to send OTP');
          return;
        }
        setOtpCode('');
        submittedCodeRef.current = '';
        setCountdown(60);
        toast({
          variant: 'success',
          title: 'OTP Sent!',
          description: 'Check your WhatsApp for the verification code.',
        });
      } catch (err: any) {
        setOtpError(friendlyMessage(err, 'Failed to send OTP'));
      } finally {
        setSending(false);
      }
    };

    // ── Screen 2 → branch on the resolved answer ───────────────────────────
    const submitCode = async () => {
      if (otpCode.length !== 6) {
        setOtpError('Please enter the 6-digit code sent to your WhatsApp');
        return;
      }
      if (userExists === null) {
        resetToPhone('Please re-enter your phone number.');
        return;
      }
      setOtpError('');
      setVerifying(true);
      try {
        if (userExists) {
          // EXISTING ACCOUNT — straight to login-otp. Never /verify-otp here.
          const result = await loginWithOtp(phoneNumber, otpCode.trim());
          if (result.success) {
            if (result.requires_totp && result.userId) {
              onRequiresTotp(result.userId);
              return;
            }
            onSuccess();
            return;
          }
          if (result.errorCode === 'PHONE_AMBIGUOUS') {
            // Several accounts share this number — OTP cannot pick one.
            onSwitchToPassword?.(
              result.error || 'Multiple accounts use this phone number. Please sign in with email and password.',
            );
            return;
          }
          if (result.errorCode === 'OTP_LOCKED') {
            resetToPhone(result.error || 'Too many incorrect attempts. Please request a new code.');
            return;
          }
          // INVALID_OTP and friends: inline error, code cleared, resend intact.
          setOtpError(result.error || 'Login failed');
          setOtpCode('');
          submittedCodeRef.current = '';
          return;
        }

        // NEW NUMBER — verify the code, then collect the name on Screen 3.
        const result = await authApi.verifyOTP(phoneNumber, otpCode.trim());
        if (result.data?.verified) {
          toast({
            variant: 'success',
            title: 'Phone verified!',
            description: 'Your phone number has been verified successfully.',
          });
          setStep('name');
          return;
        }
        if (result.errorCode === 'OTP_LOCKED') {
          resetToPhone(result.error || 'Too many incorrect attempts. Please request a new code.');
          return;
        }
        setOtpError(result.error || 'Invalid OTP code');
        setOtpCode('');
        submittedCodeRef.current = '';
      } catch (err: any) {
        setOtpError(friendlyMessage(err, 'Verification failed'));
        setOtpCode('');
        submittedCodeRef.current = '';
      } finally {
        setVerifying(false);
      }
    };

    // Auto-submit once all 6 digits are in — the segmented input has no explicit
    // verify button. The ref makes it fire exactly once per completed code; it is
    // reset whenever the code is cleared, so a corrected code re-submits.
    useEffect(() => {
      if (
        step === 'otp' &&
        otpCode.length === 6 &&
        !verifying &&
        !creating &&
        submittedCodeRef.current !== otpCode
      ) {
        submittedCodeRef.current = otpCode;
        submitCode();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpCode, step]);

    // ── Screen 3 → create the account ─────────────────────────────────────
    // Referral code from the invite link (?ref= / ?referral_code=). No manual
    // entry — the organization step was removed for phone-first onboarding.
    const referralCodeFromUrl = searchParams.get('ref') || searchParams.get('referral_code') || '';

    const handleCreateAccount = async () => {
      if (!organizationName.trim()) {
        setNameError('Please enter your organization name');
        return;
      }
      setNameError('');
      setCreating(true);
      try {
        const orgName = organizationName.trim();
        const result = await signupWithPhone(
          // full_name → profiles.full_name; organizationName → organizations.name.
          // Same value: signup collects one name and it is the organization's.
          orgName,
          phoneNumber,
          otpCode.trim(),
          referralCodeFromUrl || undefined,
          orgName,
        );
        if (result.success) {
          toast({
            title: 'Welcome to WeNext!',
            description: "Let's personalise your experience with a few quick questions.",
          });
          markNewSignupPending();
          // Going through /home and hoping the first-run gate redirects is racy —
          // Auth's "already signed in" effect also navigates — so land on the
          // questions directly.
          navigate(ONBOARDING_V2_PREFLIGHT_PATH);
          return;
        }
        if (result.errorCode === 'PHONE_EXISTS') {
          // An account appeared on this number between send and register. The
          // held code is stamped 'signup' and cannot log in, so a fresh code is
          // required — don't dead-end on this screen.
          resetToPhone('An account was just created with this number. Please log in with a new code.');
          return;
        }
        if (
          result.errorCode === 'PHONE_VERIFICATION_EXPIRED' ||
          result.errorCode === 'PHONE_NOT_VERIFIED'
        ) {
          resetToPhone(result.error || 'Phone verification expired. Please verify again.');
          return;
        }
        setNameError(result.error || 'Signup failed');
      } catch (err: any) {
        setNameError(friendlyMessage(err, 'Setup failed'));
      } finally {
        setCreating(false);
      }
    };

    // ── Render ────────────────────────────────────────────────────────────
    const liveOverLengthError = phoneOverLengthError(countryCode, phoneDisplay);

    if (step === 'phone') {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="auth-phone">Phone number</Label>
            <PhoneNumberInput
              id="auth-phone"
              className="h-12"
              countryCode={countryCode}
              phoneDisplay={phoneDisplay}
              invalid={!!phoneError || !!liveOverLengthError}
              onCountryChange={(code) => {
                setCountryCode(code);
                if (phoneError) setPhoneError('');
              }}
              onPhoneChange={(d) => {
                setPhoneDisplay(d);
                if (phoneError) setPhoneError('');
              }}
            />
            {(phoneError || liveOverLengthError) && (
              <p className="text-xs text-red-600">{phoneError || liveOverLengthError}</p>
            )}
          </div>
          <Button
            type="button"
            className="w-full !h-12 font-bold"
            onClick={handleSendOtp}
            disabled={sending}
          >
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
        </div>
      );
    }

    if (step === 'otp') {
      return (
        <div className="space-y-4">
          <OtpInput
            value={otpCode}
            onChange={(v) => {
              setOtpCode(v);
              if (otpError) setOtpError('');
            }}
            invalid={!!otpError}
            disabled={verifying}
            autoFocus
          />

          {otpError && <p className="text-xs text-red-600">{otpError}</p>}
          {verifying && (
            <p className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
            </p>
          )}

          <div className="flex items-center justify-end pt-1">
            <button
              type="button"
              onClick={handleResend}
              disabled={countdown > 0 || sending}
              className="text-sm font-semibold text-[var(--secondary-color)] hover:underline disabled:no-underline disabled:opacity-60"
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
            </button>
          </div>
        </div>
      );
    }

    // step === 'name' — new accounts only.
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="auth-organization-name">Organization name</Label>
          <Input
            id="auth-organization-name"
            type="text"
            placeholder="Your organization name"
            value={organizationName}
            onChange={(e) => {
              setOrganizationName(e.target.value);
              if (nameError) setNameError('');
            }}
            aria-invalid={!!nameError}
            required
            autoFocus
            disabled={creating}
            className={cn('h-12', nameError && 'border-red-400 focus-visible:ring-red-400')}
          />
          {nameError && <p className="text-xs text-red-600">{nameError}</p>}
        </div>

        <Button
          type="button"
          className="w-full !h-12 font-bold"
          onClick={handleCreateAccount}
          disabled={creating}
        >
          {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create account
        </Button>

        {/* Consent line — terms are accepted implicitly by submitting. */}
        <p className="text-center text-xs text-gray-600">
          By clicking Create account, you agree to our{' '}
          <button
            type="button"
            onClick={() => setShowTermsDialog(true)}
            className="text-[var(--secondary-color)] hover:underline"
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--secondary-color)] hover:underline"
          >
            Privacy Policy
          </a>.
        </p>

        <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <LandingTermsOfService hideHeader={true} />
          </DialogContent>
        </Dialog>
      </div>
    );
  },
);
